import React, {useState, useEffect} from "react";
import {useLocation} from "react-router-dom";
import {IoAddCircleOutline} from "react-icons/io5";
import {withTranslation} from "react-i18next";
import AddSpecificationModal from "../../components/filling/AddSpecificationModal";
import {useSelector} from "react-redux/es/exports";
import {createSpecification} from "../../services/specificationService";
import {useNavigate} from "react-router-dom";
import EditButton from "../../components/EditButton";
import DeleteButton from "../../components/DeleteButton";
import Select from "../../components/Select";
import Pagination from "../../components/Pagination";
import FillingDataTable from "../../components/FillingDataTable";
import swal from "sweetalert";
import {deleteSpecification} from "../../services/specificationService";
import Loading from "../../components/loading";
import {toast} from "react-toastify";
import TemDataTableInline from "../../components/TemDataTableInline";


const headerTable = [{
    name: "id", sort: false
}, {
    name: "thickness", sort: false
}, {
    name: "color", sort: true
}, {
    name: "product_name", sort: true
}, {
    name: "supplier_name", sort: true
}, {
    name: "article", sort: true
}, <span className="sr-only">Edit</span>,];

const bodyTable = [
    "id",
    "thickness",
    "color",
    "productName",
    "supplierName",
    "article",
    "actions",
];

const FillingSpecification = ({t, id, selectedRowId, setSelectedRowId}) => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [itemOffset, setItemOffset] = useState(0);
    const [forcePage, setForcePage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [sortColumn, setSortColumn] = useState("id");
    const [sortDir, setSortDir] = useState("DESC");
    const [clear, setClear] = useState(false);
    const [data, setData] = useState({
        data: [],
        recordsTotal: 0,
        pageCount: 1,
    });
    const [rowId, setRowId] = useState(null);
    const {token} = useSelector((state) => state.user);
    const navigate = useNavigate();

    const deleteHandler = async (id) => {
        const responseReceived = await swal({
            title: t(`action.delete_item.title`),
            text: t(`action.delete_item.text`),
            icon: "warning",
            buttons: {
                cancel: true,
                confirm: true,
            },
        });
        if (responseReceived) {
            setIsLoading(true);
            const response = await deleteSpecification(
                `${process.env.REACT_APP_HOST_BASE_URL}/v1/filling/specification/group/${id}`,
                token
            );
            if (response.success) {
                setIsLoading(false);
                fetchData();
                toast.success(t('action.delete_success'), {
                    position: "bottom-right",
                    autoClose: 3000,
                    closeOnClick: true,
                    className: "add-success"
                });
            } else {
                navigate("/error", {
                    state: {
                        status: response.status,
                        message: response.message ? response.message : null,
                    },
                });
            }
        } else {
            return;
        }
    };

    const fetchData = async () => {
        const response = await createSpecification(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/filling/specification/group/table`,
            token,
            {
                draw: 1,
                start: itemOffset,
                length: itemsPerPage,
                recordsTotal: 0,
                columns: [],
                order: [{column: sortColumn, dir: sortDir}],
                search: {value: "", regex: true},
                filter: {
                    fillingGroupId: id,
                },
            }
        );
        if (response.success) {
            setIsLoading(false);
            setClear(false);
            let reviewData = response.data.data.map((item, index) => {
                return {
                    id: item.id,
                    thickness: item.thickness,
                    color: item?.textureName,
                    productName: item.productName,
                    supplierName: item.supplierName,
                    article: item.article,
                    actions: (
                        <div className="flex items-center w-full">
                            <EditButton onclick={() => updateHandler(item.id)}/>
                            <DeleteButton onclick={() => deleteHandler(item.id)}/>
                        </div>
                    ),
                };
            });
            setData({
                data: reviewData,
                recordsTotal: response.data.recordsFiltered,
                pageCount: Math.ceil(
                    response.data.recordsFiltered / parseInt(itemsPerPage)
                ),
            });
        } else {
            setIsLoading(false);
            navigate("/error", {
                state: {
                    status: response.status,
                    message: response.message ? response.message : null,
                },
            });
        }
    };

    const updateHandler = (id) => {
        setSelectedRowId(id);
        setRowId(id);
        setOpen(true);
    };

    const handlePageClick = (event) => {
        setForcePage(event.selected);
        const newOffset =
            (event.selected * parseInt(itemsPerPage)) % data.recordsTotal;
        setItemOffset(newOffset);
    };

    useEffect(() => {
        if (clear) {
            fetchData();
        }
    }, [clear]);

    useEffect(() => {
        fetchData();
    }, [itemOffset, itemsPerPage, data.recordsTotal]);

    const sortFunction = (value) => {
        setSortColumn(value.field ? value.field : "id");
        setSortDir(value.sort === true ? 'ASC' : 'DESC');
        setClear(true);
    }

    return (
        <>
            {isLoading ? (
                <Loading/>
            ) : (
                <>
                    <AddSpecificationModal
                        fillingGroupId={id}
                        open={open}
                        setOpen={setOpen}
                        rowId={rowId}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                        fetchData={fetchData}
                    />
                    <div className="sm:flex-auto">
                        <h1 className="text-xl font-semibold text-gray-900">
                            {t(`table.filling.filling_specification`)}
                        </h1>
                    </div>
                    <div className="flex justify-between">
                        <Select
                            setItemsPerPage={setItemsPerPage}
                            itemsPerPage={itemsPerPage}
                        />
                        <button
                            className="flex items-center px-3 py-1 text-sm font-medium leading-4 text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            onClick={() => {
                                setSelectedRowId(null);
                                setOpen(true);
                            }}
                        >
                            {t('action.add')}
                            <IoAddCircleOutline className="w-4 h-4 ml-1"/>
                        </button>
                    </div>

                    <TemDataTableInline
                        data={data.data}
                        headerTable={headerTable}
                        bodyContent={bodyTable}
                        rowHandler={(id) => {
                            setSelectedRowId(id);
                        }}
                        sortFunction={sortFunction}
                    />
                    <Pagination
                        handlePageClick={handlePageClick}
                        pageCount={data.pageCount}
                        forcePage={forcePage}
                    />
                </>
            )}
        </>
    );
};

export default withTranslation()(FillingSpecification);
